import {
  Checkbox,
  CheckboxChangeEvent,
  InputChangeEvent,
  TextAreaChangeEvent,
  NumericTextBox,
  NumericTextBoxChangeEvent
} from '@progress/kendo-react-inputs';
import { ComponentType, ReactNode } from 'react';
import { Label } from '@progress/kendo-react-labels';

import {
  BankItemGroup,
  ItemEntity,
  ResourceEntity
} from '../../../types/game-document/';
import ImageResource from '../image-resource/resource';
import { EditorMode } from '../../../types/editor-mode';
import { StandardInput } from '../../../components/forms';
import RequiredFields from '../../../types/required-fields';
import { TextareaInput } from '../../../components/form-input';
import BankItemGroupComboBox from '../../../components/combobox/bank-item-group-combobox';

interface ItemEditorProps {
  editorMode: EditorMode;
  entity: ItemEntity;
  resources: ResourceEntity[];
  handleInputChange: (event: InputChangeEvent) => void;
  handleImageSelected?:
    | ((url: string, size: number, name?: string, category?: string) => void)
    | (() => {});
  children?: ReactNode | undefined;
  imageResourceVisibility?: boolean | undefined;
  requiredFields?: RequiredFields<ItemEntity>[];
  handleHideItemChange: (event: CheckboxChangeEvent) => void;
  handleTextAreaChange: (event: TextAreaChangeEvent) => void;
  bankItemGroups?: BankItemGroup[];
  onRemoveBankItemGroup?: (index: number) => void;
  onAddBankItemGroup?: (group: BankItemGroup) => void;
  onUpdateBankItemGroup?: (index: number, group: BankItemGroup) => void;
  onBankItemGroupChange?: (id?: string) => void;
  checkBankItemGroupUsage?: (id: string) => number;
}

export const ItemEditor: ComponentType<ItemEditorProps> = ({
  editorMode,
  entity,
  resources,
  handleInputChange,
  handleImageSelected = () => {},
  imageResourceVisibility,
  requiredFields,
  handleHideItemChange,
  handleTextAreaChange,
  bankItemGroups = [],
  onRemoveBankItemGroup = (index: number) => {},
  onAddBankItemGroup = (group: BankItemGroup) => {},
  onUpdateBankItemGroup = (index: number, group: BankItemGroup) => {},
  onBankItemGroupChange = (id?: string) => {},
  checkBankItemGroupUsage,
  ...props
}: ItemEditorProps) => {
  const handleSelectImage = (
    url: string,
    size: number,
    name?: string,
    category?: string
  ) => {
    handleImageSelected(url, size, name, category);
  };

  if (!entity) return;

  return (
    <>
      <div className={'d-flex flex-row'}>
        <div className={'flex-column w-10 me-2'}>
          <ImageResource
            title={'Image'}
            imageResource={
              resources.find((item) => item.id === entity.imageResId)!
            }
            onSubmit={handleSelectImage}
            source="Inventory"
            defaultSelectedCategory={'Inventory images'}
          />
        </div>
        <div className="d-flex mt-4 me-3">
          <div
            style={{
              borderRight: '2px solid #e9e9e9', // Vertical line style
              height: '60%' // Full height of the parent
            }}></div>
        </div>
        <div className={'flex-column flex-fill'}>
          <StandardInput
            name={'titleResId'}
            label={'Title'}
            value={
              resources.find((resource) => resource.id === entity?.titleResId)
                ?.value ?? ''
            }
            onChange={handleInputChange}
            validationMessage={
              requiredFields?.find((item) => item.name === 'titleResId')
                ?.errorMessage
            }
          />
          <TextareaInput
            label={'Description'}
            name={'description'}
            value={entity?.description}
            rows={4}
            onChange={handleTextAreaChange}
          />

          <StandardInput
            name={'summaryResId'}
            label={'Summary'}
            value={
              resources.find((resource) => resource.id === entity?.summaryResId)
                ?.value ?? ''
            }
            onChange={handleInputChange}
          />
          <Checkbox
            name={'hideInGame'}
            label={'Hide inventory items from players.'}
            size={'large'}
            value={entity?.hideInGame}
            onChange={handleHideItemChange}
          />
          <Checkbox
            name={'useBank'}
            label={'Use bank'}
            size={'large'}
            value={entity?.useBank}
            onChange={handleHideItemChange}
          />
          {entity?.useBank && (
            <div className={'pb-2'}>
              <Label
                style={{
                  display: 'block',
                  marginBottom: '4px'
                }}>
                Maximum stock
              </Label>
              <NumericTextBox
                name={'maxStock'}
                style={{ width: '300px' }}
                value={entity?.maxStock}
                onChange={(e: NumericTextBoxChangeEvent) => {
                  handleInputChange({
                    target: { name: e.target.name, value: e.target.value }
                  } as any);
                }}
                min={0}
              />
              <div className={'mt-2'}>
                <Label
                  style={{
                    display: 'block',
                    marginBottom: '4px'
                  }}>
                  Group
                </Label>
                <BankItemGroupComboBox
                  style={{ width: '300px' }}
                  data={bankItemGroups}
                  value={entity?.bankItemGroupId}
                  onRemoveBankItemGroup={onRemoveBankItemGroup}
                  onAddBankItemGroup={onAddBankItemGroup}
                  onUpdateBankItemGroup={onUpdateBankItemGroup}
                  onChange={onBankItemGroupChange}
                  checkUsageCount={checkBankItemGroupUsage}
                />
              </div>
            </div>
          )}
        </div>
      </div>

      {props.children}
    </>
  );
};
